import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { PremiumHeader } from '@/components/premium/PremiumHeader';
import { PremiumFooter } from '@/components/premium/PremiumFooter';
import { SEO } from '@/components/SEO';
import { Calendar, Clock, ArrowRight, Search, Loader2 } from 'lucide-react';
import { PageFAQ } from '@/components/PageFAQ';
import { RelatedPages } from '@/components/RelatedPages';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = ['All', 'Buying Guide', 'Planning Guide', 'Cost Management', 'Privacy & Acoustic Solutions', 'Workplace Wellness', 'Expert Advice', 'Strategic Planning'];

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/blog`);
      if (response.ok) {
        const data = await response.json();
        setPosts(data);
      }
    } catch (error) {
      console.error('Error fetching blog posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredPosts = posts.filter(post => {
    const matchesCategory = activeCategory === 'All' || post.category === activeCategory;
    const matchesSearch = !searchQuery || 
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.excerpt?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const featuredPost = filteredPosts.find(post => post.featured) || filteredPosts[0];
  const regularPosts = filteredPosts.filter(post => post !== featuredPost);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <PremiumHeader />
        <div className="container mx-auto px-8 py-32 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#c5a059]" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <SEO 
        title="Blog | Office Furniture Insights | Modular Furniture Store"
        description="Expert articles on modular office furniture, workspace planning, privacy booths, workstations, and modern office trends. Your guide to smarter workspace decisions."
        keywords="modular office furniture blog, workstation guide, privacy booth benefits, office furniture vs carpentry, open office design"
        canonical="/blog"
      />
      <PremiumHeader />
      
      {/* Hero Section */}
      <section className="pt-28 pb-12 bg-[#f8f7f4]">
        <div className="container mx-auto px-8">
          <div className="max-w-3xl">
            <p className="text-[#c5a059] text-sm tracking-[0.3em] uppercase mb-4 font-medium">
              Knowledge Hub
            </p>
            <h1 
              className="text-4xl md:text-5xl font-bold text-[#1a1a1a] mb-4"
              
            >
              Office Furniture <span className="text-[#c5a059]">Insights</span>
            </h1>
            <p className="text-gray-600 text-lg">
              Expert guides, industry insights, and practical advice to help you make informed decisions about your workspace.
            </p>
          </div>
        </div>
      </section>

      {/* Search & Filter */}
      <section className="border-b border-gray-200 sticky top-[85px] z-40 bg-white">
        <div className="container mx-auto px-8 py-4">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            {/* Categories */}
            <div className="flex flex-wrap gap-2 flex-1" data-testid="blog-categories">
              {CATEGORIES.map(cat => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-4 py-2 text-sm font-medium transition-all ${
                    activeCategory === cat
                      ? 'bg-[#1a1a1a] text-white'
                      : 'bg-transparent text-gray-600 hover:text-[#1a1a1a] hover:bg-gray-100'
                  }`}
                  data-testid={`blog-category-${cat.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {cat}
                </button>
              ))}
            </div>
            {/* Search */}
            <div className="relative w-full md:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 text-sm focus:outline-none focus:border-[#c5a059] transition-colors"
                data-testid="blog-search"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Blog Content */}
      <section className="py-12">
        <div className="container mx-auto px-8">
          {filteredPosts.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-xl text-gray-400">No articles found.</p>
              <p className="text-sm text-gray-400 mt-2">Try a different category or search term.</p>
            </div>
          ) : (
            <>
              {/* Featured Post */}
              {featuredPost && (
                <Link 
                  to={`/blog/${featuredPost.slug}`}
                  className="group block mb-12"
                  data-testid="blog-featured-post"
                >
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 border border-gray-200 hover:border-[#c5a059]/50 transition-all overflow-hidden">
                    <div className="relative overflow-hidden">
                      <img 
                        src={featuredPost.image_url} 
                        alt={featuredPost.title}
                        className="w-full h-64 lg:h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        loading="lazy"
                      />
                      <span className="absolute top-4 left-4 bg-[#c5a059] text-[#1a1a1a] px-3 py-1 text-xs font-bold uppercase tracking-wider">
                        Featured
                      </span>
                    </div>
                    <div className="p-8 lg:p-12 flex flex-col justify-center bg-white">
                      <span className="text-[#c5a059] text-xs font-semibold uppercase tracking-[0.2em] mb-3">
                        {featuredPost.category}
                      </span>
                      <h2 
                        className="text-2xl lg:text-3xl font-bold text-[#1a1a1a] mb-4 group-hover:text-[#c5a059] transition-colors"
                        
                      >
                        {featuredPost.title}
                      </h2>
                      <p className="text-gray-600 mb-6 leading-relaxed line-clamp-3">
                        {featuredPost.excerpt}
                      </p>
                      <div className="flex items-center gap-6 text-sm text-gray-400">
                        <span className="flex items-center gap-1.5">
                          <Calendar className="w-4 h-4" />
                          {formatDate(featuredPost.created_at)}
                        </span>
                        <span className="flex items-center gap-1.5">
                          <Clock className="w-4 h-4" />
                          {featuredPost.read_time}
                        </span>
                      </div>
                      <span className="inline-flex items-center gap-2 text-[#c5a059] font-semibold text-sm mt-6 group-hover:gap-3 transition-all">
                        Read Article <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </Link>
              )}

              {/* Grid of Posts */}
              {regularPosts.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {regularPosts.map((post) => (
                    <Link 
                      key={post.id}
                      to={`/blog/${post.slug}`}
                      className="group block border border-gray-200 hover:border-[#c5a059]/50 hover:shadow-md transition-all duration-300 overflow-hidden"
                      data-testid={`blog-post-${post.slug}`}
                    >
                      <div className="relative overflow-hidden">
                        <img 
                          src={post.image_url} 
                          alt={post.title}
                          className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                        />
                        <span className="absolute top-3 left-3 bg-white/90 text-[#1a1a1a] px-3 py-1 text-[10px] font-bold uppercase tracking-wider">
                          {post.category}
                        </span>
                      </div>
                      <div className="p-6">
                        <h3 
                          className="text-lg font-bold text-[#1a1a1a] mb-2 group-hover:text-[#c5a059] transition-colors line-clamp-2"
                          
                        >
                          {post.title}
                        </h3>
                        <p className="text-gray-500 text-sm mb-4 line-clamp-2 leading-relaxed">
                          {post.excerpt}
                        </p>
                        <div className="flex items-center justify-between text-xs text-gray-400 pt-4 border-t border-gray-100">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(post.created_at)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {post.read_time}
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#f8f7f4]">
        <div className="container mx-auto px-8 text-center">
          <h2 
            className="text-3xl font-bold text-[#1a1a1a] mb-4"
            
          >
            Need Expert <span className="text-[#c5a059]">Advice</span>?
          </h2>
          <p className="text-gray-600 mb-8 max-w-xl mx-auto">
            Our furniture consultants can help you plan the perfect workspace. Get personalized recommendations based on your team size and budget.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <button className="bg-[#c5a059] hover:bg-[#b8934d] text-[#1a1a1a] px-8 py-3 font-bold text-sm tracking-[0.1em] uppercase transition-colors">
                Contact Us
              </button>
            </Link>
            <Link to="/bulk-inquiry">
              <button className="border-2 border-[#1a1a1a] text-[#1a1a1a] hover:bg-[#1a1a1a] hover:text-white px-8 py-3 font-bold text-sm tracking-[0.1em] uppercase transition-colors">
                Request Quote
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ + Interlinks */}
      <div className="container mx-auto px-4 sm:px-8 py-12 bg-white">
        <PageFAQ pageKey="blog" />
        <RelatedPages pageKey="blog" className="mt-12" />
      </div>

      <PremiumFooter />
    </div>
  );
}
